import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Bot, User, Sparkles, ThumbsUp, ThumbsDown, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../apiConfig';

// Cleanly format bot/user message text, strip asterisks around keywords, parse links and URLs safely, and prevent text overflow
const renderFormattedMessage = text => {
  if (!text) return null;

  const lines = text.split('\n');

  return lines.map((line, lineIdx) => {
    if (!line.trim()) {
      return <div key={lineIdx} className="h-1.5" />;
    }

    // Process inline markdown elements
    const parseInline = str => {
      // 1. Match markdown links like [label](url) or [label] (url)
      const linkParts = [];
      let lastIndex = 0;
      const combinedLinkRegex = /\[([^\]]+)\]\s*\((https?:\/\/[^\s)]+)\)/g;
      let match;

      while ((match = combinedLinkRegex.exec(str)) !== null) {
        if (match.index > lastIndex) {
          linkParts.push({ type: 'text', content: str.substring(lastIndex, match.index) });
        }
        linkParts.push({
          type: 'link',
          label: match[1],
          url: match[2],
        });
        lastIndex = combinedLinkRegex.lastIndex;
      }
      if (lastIndex < str.length) {
        linkParts.push({ type: 'text', content: str.substring(lastIndex) });
      }

      // 2. Parse bold (**keyword**) and bare URLs / emails
      return linkParts.flatMap((part, pIdx) => {
        if (part.type === 'link') {
          return (
            <a
              key={`link-${pIdx}`}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-300 hover:text-cyan-200 underline font-semibold break-all inline-flex items-center gap-0.5 mx-0.5"
            >
              <span>{part.label}</span>
              <span className="text-[10px]">↗</span>
            </a>
          );
        }

        // Bold: **keyword**
        const boldParts = part.content.split(/(\*\*[^*]+\*\*)/g);
        return boldParts.flatMap((bPart, bIdx) => {
          if (bPart.startsWith('**') && bPart.endsWith('**')) {
            const cleanKeyword = bPart.slice(2, -2).trim();
            return (
              <strong key={`b-${pIdx}-${bIdx}`} className="font-bold text-cyan-200">
                {cleanKeyword}{' '}
              </strong>
            );
          }

          // Bare URLs: https://... or http://...
          const urlParts = bPart.split(/(https?:\/\/[^\s]+)/g);
          return urlParts.flatMap((uPart, uIdx) => {
            if (/^https?:\/\//.test(uPart)) {
              const cleanUrl = uPart.replace(/[),.;]+$/, '');
              return (
                <a
                  key={`url-${pIdx}-${bIdx}-${uIdx}`}
                  href={cleanUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-300 hover:text-cyan-200 underline break-all inline-block"
                >
                  {cleanUrl}
                </a>
              );
            }

            // Emails: name@domain.com
            const emailParts = uPart.split(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g);
            return emailParts.map((ePart, eIdx) => {
              if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(ePart)) {
                return (
                  <a
                    key={`email-${pIdx}-${bIdx}-${uIdx}-${eIdx}`}
                    href={`mailto:${ePart}`}
                    className="text-cyan-300 hover:text-cyan-200 underline break-all font-medium"
                  >
                    {ePart}
                  </a>
                );
              }

              // Clean any remaining lone asterisks before keywords (e.g. "* Keyword" -> "• Keyword")
              const cleanStr = ePart.replace(/(?:^|\s)\*(\s*)/g, ' • $1').replace(/\*\*/g, '');
              return <span key={`txt-${pIdx}-${bIdx}-${uIdx}-${eIdx}`}>{cleanStr}</span>;
            });
          });
        });
      });
    };

    return (
      <div key={lineIdx} className="break-words break-all [overflow-wrap:anywhere] leading-relaxed">
        {parseInline(line)}
      </div>
    );
  });
};

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(new Set());
  const [sessionQueries, setSessionQueries] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Show expand animation on every page load/refresh
  useEffect(() => {
    const expandTimer = setTimeout(() => {
      setIsExpanded(true);
    }, 800);
    const collapseTimer = setTimeout(() => {
      setIsExpanded(false);
    }, 3800);
    return () => {
      clearTimeout(expandTimer);
      clearTimeout(collapseTimer);
    };
  }, []);

  // Hide chatbot button when registration modal or mobile nav menu is open
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setModalOpen(document.body.classList.contains('modal-open'));
      setNavMenuOpen(document.body.classList.contains('nav-menu-open'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Load chatbot data and initial greeting
  useEffect(() => {
    const fetchChatbotData = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/chatbot/data`);
        const data = await res.json();

        if (messages.length === 0) {
          setMessages([
            {
              id: Date.now(),
              text: data.welcomeMessage || "Hi! I'm VortexBot. How can I help you today?",
              sender: 'bot',
              timestamp: new Date(),
            },
          ]);
        }
      } catch (err) {
        console.error('Error fetching chatbot data:', err);
        if (messages.length === 0) {
          setMessages([
            {
              id: Date.now(),
              text: "Hi! I'm VortexBot. How can I help you today?",
              sender: 'bot',
              timestamp: new Date(),
            },
          ]);
        }
      }
    };

    fetchChatbotData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket Back-Forward Cache cleanup: detect page enter/exit cache, close stale connections
  useEffect(() => {
    const handlePageShow = event => {
      if (event.persisted) {
        // Page restored from Back-Forward Cache
        console.debug('Page restored from Back-Forward Cache');
        // Any stale WebSocket connections will throw "Page entered Back-Forward Cache" error
        // This is expected and handled by error handlers in server-side WebSocket code
      }
    };

    const handlePageHide = event => {
      if (event.persisted) {
        console.debug('Page being cached (persisted)');
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Focus input when chat opens (non-iOS to avoid keyboard jump)
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (!isIOS) {
        setTimeout(() => inputRef.current?.focus(), 300);
      }
    }
  }, [isOpen]);

  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const currentQuery = inputMessage;
    setInputMessage('');
    setIsTyping(true);

    try {
      const previousQuery =
        sessionQueries.length > 0 ? sessionQueries[sessionQueries.length - 1] : null;

      const res = await fetch(`${API_BASE_URL}/api/chatbot/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: currentQuery,
          previousQuery,
          sessionQueries,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || `Server error: ${res.status}`);
      }

      setSessionQueries(prev => [...prev, currentQuery]);

      const botMessage = {
        id: Date.now() + 1,
        text: data.response || "I received a response but couldn't process it. Please try again.",
        sender: 'bot',
        timestamp: new Date(),
        suggestions: data.suggestions || [],
        navigationLinks: data.navigationLinks || [],
        responseId: data.responseId,
        learnedId: data.learnedId,
        faqId: data.faqId,
        confidence: data.confidence,
        intent: data.intent,
        askFeedback: data.askFeedback,
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    } catch (err) {
      console.error('Error sending message:', err);
      setIsTyping(false);

      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          text: "Sorry, I'm having trouble connecting. Please try again later or contact us directly.",
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    }
  }, [inputMessage, sessionQueries]);

  const handleSuggestionClick = useCallback(suggestion => {
    setInputMessage(suggestion);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Navigate to a page and close the chatbot
  const handleNavigationLink = useCallback(
    url => {
      setIsOpen(false);
      setTimeout(() => navigate(url), 200);
    },
    [navigate]
  );

  const handleFeedback = async (messageId, wasHelpful) => {
    if (feedbackGiven.has(messageId)) return;

    const message = messages.find(m => m.id === messageId);
    if (!message) return;

    setFeedbackGiven(prev => new Set([...prev, messageId]));

    setMessages(prev => [
      ...prev,
      {
        id: Date.now(),
        text: wasHelpful
          ? '🙏 Thanks for the thumbs up! Glad I could help.'
          : "🙏 Thanks for the feedback! I'll use it to improve.",
        sender: 'bot',
        timestamp: new Date(),
        isSystemMessage: true,
      },
    ]);

    try {
      await fetch(`${API_BASE_URL}/api/chatbot/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responseId: message.responseId,
          learnedId: message.learnedId,
          faqId: message.faqId,
          wasHelpful,
          rating: wasHelpful ? 5 : 1,
        }),
      });
    } catch (err) {
      console.error('Error submitting feedback:', err);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chatbot Toggle Button (FAB) */}
      <AnimatePresence>
        {!isOpen && !modalOpen && !navMenuOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0, rotate: -180 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: 0, opacity: 0, rotate: 180 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            className="fixed z-[9999] flex items-center cursor-pointer chatbot-fab outline-none focus:outline-none"
            style={{
              bottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))',
              right: 'max(1rem, env(safe-area-inset-right, 1rem))',
            }}
            onClick={() => setIsOpen(true)}
          >
            {/* Expanded label — slides in towards the left */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  key="chatbot-label"
                  initial={{ maxWidth: 0, opacity: 0 }}
                  animate={{ maxWidth: 160, opacity: 1 }}
                  exit={{ maxWidth: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden mr-2"
                  style={{ willChange: 'max-width, opacity' }}
                >
                  <div
                    className="flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap"
                    style={{
                      background: 'linear-gradient(135deg, #0f0f1a, #1a1a2e)',
                      border: '1px solid rgba(0,212,255,0.4)',
                      boxShadow: '0 0 12px rgba(0,212,255,0.2)',
                    }}
                  >
                    <Sparkles className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#FF3CAC' }} />
                    <span className="text-sm font-bold tracking-wide" style={{ color: '#00D4FF' }}>
                      Vortex Bot
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* FAB icon button */}
            <div
              className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center flex-shrink-0"
              aria-label="Open Chat"
            >
              {/* Spinning conic-gradient ring — GPU-composited */}
              <div className="absolute inset-0 rounded-full chatbot-spin-ring" />

              {/* Inner circle */}
              <div
                className="absolute rounded-full"
                style={{ inset: '3px', background: '#0a0a14' }}
              />

              {/* Icon */}
              <div className="relative z-10 flex flex-col items-center justify-center gap-0.5">
                <Bot
                  className="w-6 h-6 sm:w-7 sm:h-7"
                  style={{
                    color: '#00D4FF',
                    filter: 'drop-shadow(0 0 5px rgba(0,212,255,0.8))',
                  }}
                />
                <span
                  className="text-[6px] font-black tracking-wider"
                  style={{ color: 'rgba(0,212,255,0.8)' }}
                >
                  Chatbot
                </span>
              </div>

              {/* Live dot */}
              <span
                className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0f0f1a] animate-pulse"
                style={{ background: '#00FF88', boxShadow: '0 0 6px #00FF88' }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chatbot Window — RESPONSIVE FOR ALL DEVICES */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="glass-card flex flex-col overflow-hidden border-2 border-cyan-500/30 fixed z-[9999]
              /* Mobile: full screen */
              inset-0 rounded-none
              /* Desktop (sm+): floating bottom-right panel */
              sm:inset-auto sm:rounded-2xl
              sm:bottom-6 sm:right-6
              sm:w-96 sm:h-[600px] sm:max-h-[calc(100dvh-5rem)]"
            style={{
              boxShadow: '0 0 40px rgba(6, 182, 212, 0.3), 0 20px 60px rgba(0, 0, 0, 0.5)',
              /* iOS safe area at bottom on full-screen mobile */
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            {/* Header */}
            <div
              className="bg-gradient-animated px-3 pb-3 sm:p-4 flex items-center justify-between flex-shrink-0 relative overflow-hidden"
              style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0.75rem))' }}
            >
              {/* Animated wave background */}
              <div className="absolute inset-0 bg-wave-animated opacity-30"></div>

              {/* Floating particles */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="particle-float particle-1"></div>
                <div className="particle-float particle-2"></div>
                <div className="particle-float particle-3"></div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 relative z-10">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm ring-2 ring-white/30 animate-spin-slow">
                  <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm sm:text-base flex items-center gap-2">
                    VortexBot
                  </h3>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse-glow"></span>
                    <span className="text-white/90 text-xs font-medium">Online · Team Vortex</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-white active:scale-95 transition-all p-2.5 touch-manipulation bg-white/20 hover:bg-white/30 rounded-xl relative z-10 flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close Chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages Area */}
            <div
              className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 bg-gradient-to-b from-cyan-900/20 via-blue-900/30 to-purple-900/40 relative"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {/* Animated background pattern */}
              <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`flex gap-2 items-start ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.sender === 'bot' && (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 ring-2 ring-cyan-400/30 animate-pulse-glow">
                      <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                  )}

                  <div
                    className={`max-w-[85%] sm:max-w-[80%] overflow-hidden break-words break-all ${message.sender === 'user' ? 'order-1' : ''}`}
                  >
                    <div
                      className={`rounded-xl px-3.5 py-2.5 min-w-[2.5rem] break-words break-all overflow-hidden [overflow-wrap:anywhere] ${
                        message.sender === 'user'
                          ? 'bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600 text-white shadow-lg shadow-cyan-500/20'
                          : message.isSystemMessage
                            ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/10 text-green-300 border border-green-500/30 backdrop-blur-sm'
                            : 'bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-purple-500/10 text-white border border-cyan-500/30 backdrop-blur-sm'
                      }`}
                    >
                      <div className="text-sm leading-relaxed break-words break-all [overflow-wrap:anywhere] space-y-0.5">
                        {renderFormattedMessage(message.text)}
                      </div>
                    </div>

                    {/* Feedback Buttons */}
                    {message.sender === 'bot' &&
                      message.askFeedback &&
                      !message.isSystemMessage &&
                      !feedbackGiven.has(message.id) && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-white/50">Was this helpful?</span>
                          <button
                            onClick={() => handleFeedback(message.id, true)}
                            className="p-1.5 bg-green-500/20 hover:bg-green-500/30 active:bg-green-500/40 text-green-400 rounded-lg transition-all touch-manipulation min-w-[32px] min-h-[32px] flex items-center justify-center"
                            aria-label="Helpful"
                          >
                            <ThumbsUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleFeedback(message.id, false)}
                            className="p-1.5 bg-red-500/20 hover:bg-red-500/30 active:bg-red-500/40 text-red-400 rounded-lg transition-all touch-manipulation min-w-[32px] min-h-[32px] flex items-center justify-center"
                            aria-label="Not helpful"
                          >
                            <ThumbsDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}

                    {/* Feedback Given Indicator */}
                    {message.sender === 'bot' &&
                      feedbackGiven.has(message.id) &&
                      !message.isSystemMessage && (
                        <div className="mt-2 flex items-center gap-1">
                          <span className="text-xs text-green-400/70">✓ Feedback recorded</span>
                        </div>
                      )}

                    {/* Navigation Links — clickable route buttons */}
                    {message.navigationLinks && message.navigationLinks.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {message.navigationLinks.map((link, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleNavigationLink(link.url)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold
                              bg-gradient-to-r from-cyan-500/20 to-blue-500/20
                              hover:from-cyan-500/35 hover:to-blue-500/35
                              active:from-cyan-500/40 active:to-blue-500/40
                              text-cyan-300 hover:text-white
                              px-3 py-2.5 rounded-lg border border-cyan-400/30
                              transition-all touch-manipulation min-h-[44px]"
                          >
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            {link.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Text Suggestions */}
                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {message.suggestions.map((suggestion, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="block w-full text-left text-xs bg-gradient-to-r from-cyan-500/10 to-blue-500/10 active:from-cyan-500/20 active:to-blue-500/20 sm:hover:from-cyan-500/15 sm:hover:to-blue-500/15 text-white/90 active:text-white sm:hover:text-white px-2.5 sm:px-3 py-2.5 rounded-lg border border-cyan-500/20 transition-all touch-manipulation"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}

                    <p className="text-[10px] text-white/40 mt-1">
                      {message.timestamp.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>

                  {message.sender === 'user' && (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-white/20 to-white/10 rounded-full flex items-center justify-center flex-shrink-0 backdrop-blur-sm ring-2 ring-white/20">
                      <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                  )}
                </div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex gap-2 items-start justify-start">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 ring-2 ring-cyan-400/30 animate-pulse-glow">
                    <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl p-2.5 sm:p-3 border border-cyan-500/30 backdrop-blur-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce shadow-glow-cyan"></span>
                      <span
                        className="w-2 h-2 bg-blue-400 rounded-full animate-bounce shadow-glow-blue"
                        style={{ animationDelay: '0.2s' }}
                      ></span>
                      <span
                        className="w-2 h-2 bg-purple-400 rounded-full animate-bounce shadow-glow-purple"
                        style={{ animationDelay: '0.4s' }}
                      ></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-3 sm:p-4 bg-gradient-to-r from-cyan-900/40 via-blue-900/40 to-purple-900/40 border-t border-cyan-500/20 flex-shrink-0 relative">
              {/* Animated border glow */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-shimmer"></div>

              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={e => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about events, team, payments..."
                  // 16px prevents iOS Safari zoom-in on focus
                  style={{ fontSize: '16px' }}
                  className="flex-1 bg-white/10 border border-cyan-500/30 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-white/40 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all touch-manipulation backdrop-blur-sm"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="sentences"
                  spellCheck="false"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isTyping}
                  className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 text-white p-2.5 sm:p-3 rounded-xl active:scale-95 sm:hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation flex-shrink-0 shadow-lg shadow-cyan-500/30 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Send Message"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>

              {/* Powered by */}
              <div className="flex items-center justify-center gap-1 mt-2">
                <Sparkles className="w-3 h-3 text-cyan-400 animate-pulse-glow" />
                <span className="text-[10px] text-cyan-300/60">Powered by Team Vortex</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatbot;
